// components/shared/notifications/PushNotificationForm.tsx
import React from "react";
import { Send } from "lucide-react";
import { NotificationPreview } from "./NotificationPreview";
import { ImageUploader } from "./ImageUploader";

interface PushNotificationFormProps {
  title: string;
  description: string;
  imagePreview: string | null;
  onTitleChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onImageUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClearImage: () => void;
  onSubmit: (e: React.FormEvent) => void;
  sending: boolean;
  userCount: number;
  isBroadcast: boolean;
  onToggleMode: () => void;
}

export const PushNotificationForm: React.FC<PushNotificationFormProps> = ({
  title,
  description,
  imagePreview,
  onTitleChange,
  onDescriptionChange,
  onImageUpload,
  onClearImage,
  onSubmit,
  sending,
  userCount,
  isBroadcast,
  onToggleMode,
}) => (
  <div className="bg-white rounded-lg shadow-md p-6">
    <div className="flex justify-between items-center mb-4">
      <h3 className="text-lg font-semibold">
        {isBroadcast
          ? "Push notification à tous"
          : `Push notification (${userCount} utilisateur${userCount > 1 ? "s" : ""})`}
      </h3>
      {isBroadcast && (
        <button
          onClick={onToggleMode}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-lg transition-colors hover:bg-gray-300"
        >
          Envoyer à un seul utilisateur
        </button>
      )}
    </div>

    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <label className="block mb-2 text-sm font-medium text-gray-700">Titre *</label>
        <input
          type="text"
          value={title}
          onChange={(e) => onTitleChange(e.target.value)}
          className="px-4 py-2 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500"
          placeholder="Ex: Nouvelle promotion disponible"
          required
        />
      </div>

      <div>
        <label className="block mb-2 text-sm font-medium text-gray-700">Description *</label>
        <textarea
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          rows={4}
          className="px-4 py-2 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500"
          placeholder="Décrivez votre notification..."
          required
        />
      </div>

      <div>
        <label className="block mb-2 text-sm font-medium text-gray-700">Image (optionnel - max 5MB)</label>
        <ImageUploader imagePreview={imagePreview} onImageUpload={onImageUpload} onClearImage={onClearImage} />
      </div>

      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
        <p className="mb-3 text-sm font-medium text-gray-700">Aperçu:</p>
        <NotificationPreview title={title} message={description} image={imagePreview || undefined} />
      </div>

      <button
        type="submit"
        disabled={sending}
        className="flex gap-2 justify-center items-center py-3 w-full font-medium text-white bg-green-600 rounded-lg transition-colors hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
      >
        {sending ? (
          <>
            <div className="w-5 h-5 rounded-full border-2 border-white animate-spin border-t-transparent" />
            Envoi en cours...
          </>
        ) : (
          <>
            <Send className="w-5 h-5" />
            {isBroadcast
              ? "Envoyer à tous les utilisateurs"
              : `Envoyer à ${userCount} utilisateur${userCount > 1 ? "s" : ""}`}
          </>
        )}
      </button>
    </form>
  </div>
);
